// ErrorBoundary.tsx — constitution §Gestion des erreurs
// Un ErrorBoundary par section majeure (layout, page, widget). Pas un seul global.

import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";

import { reportClientError } from "@/application/utils/errorReporter";
import { TELEMETRY_SOURCES } from "@/domain/constants/telemetry-sources";

import { PageErrorFallback } from "./PageErrorFallback";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.VITE_ENV === "development") {
      console.error("[ErrorBoundary]", error, info.componentStack);
    }
    reportClientError(TELEMETRY_SOURCES.errorBoundary, error, {
      componentStack: info.componentStack ?? "",
      errorBoundary: this.constructor.name,
    });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <PageErrorFallback status={500} onRetry={this.handleReset} />
        )
      );
    }
    return this.props.children;
  }
}
